/**
 * O livro de enderecos do titular — `API §6.2`.
 *
 * ## O `If-Match` aqui NAO passa pelo cofre de versoes
 *
 * `endpoints/cart.ts` guarda o `ETag` em `core.versions` porque **existe um carrinho por
 * sessao**: "o carrinho" e um recurso singular, e um cofre com chave `'cart'` o descreve
 * inteiro. Endereco e o oposto — sao N, e a agenda inteira e devolvida por uma rota que responde
 * **um** `ETag`, o da colecao.
 *
 * Entao a versao viaja no **corpo** (`Address.version`, `API §4.1`) e quem chama monta o
 * `If-Match` a partir do endereco que tem na mao. O cofre continuaria correto para um recurso
 * singular e seria errado aqui: guardar `'address'` faria o `ETag` do ultimo endereco tocado
 * valer para todos, e o `PATCH` do segundo endereco levaria `409` — ou, pior, passaria por
 * coincidencia de versao e escreveria sobre a leitura de outra tela.
 *
 * ## Idempotencia
 *
 * Nenhuma destas rotas leva `Idempotency-Key`: a tabela do `API §3.1` nao as lista, e nao lista
 * porque nenhuma tem efeito que doa repetir. Dois toques em "Salvar endereco" criam dois
 * enderecos — o que e visivel na propria lista e desfazivel pelo `DELETE`, ao contrario de dois
 * pedidos ou duas cobrancas.
 */
import {
  AddressListResponseSchema,
  AddressSchema,
  type Address,
  type AddressListResponse,
  type CreateAddressRequest,
  type UpdateAddressRequest,
} from '@farmacia/contracts';

import type { ApiClientCore } from '../core.js';

/** `W/"address-<id>-<version>"` — a grafia e contrato (`API §4.1`), e o servidor a exige igual. */
export function addressIfMatch(address: Pick<Address, 'id' | 'version'>): string {
  return `W/"address-${address.id}-${String(address.version)}"`;
}

export interface AddressesApi {
  /**
   * `GET /v1/me/addresses`. **Do mais recente para o mais antigo** — `data[0]` e o default do
   * MVP (`API §6.2`), e nao ha campo `isDefault` a consultar.
   */
  list: (signal?: AbortSignal) => Promise<AddressListResponse>;
  /** `POST /v1/me/addresses`. `reference` obrigatorio (R-28) — vazio responde `422`. */
  create: (body: CreateAddressRequest) => Promise<Address>;
  /**
   * `PATCH /v1/me/addresses/{id}`. O segundo argumento e o endereco **como a tela o tem**: dele
   * sai o `If-Match`. Passar o objeto inteiro, e nao o `id`, e o que impede alguem de editar um
   * endereco sem ter lido a versao dele.
   */
  update: (
    address: Pick<Address, 'id' | 'version'>,
    body: UpdateAddressRequest,
  ) => Promise<Address>;
  /** `DELETE /v1/me/addresses/{id}` — soft no servidor, `204` sem corpo. */
  remove: (address: Pick<Address, 'id' | 'version'>) => Promise<void>;
}

export function createAddressesApi(core: ApiClientCore): AddressesApi {
  return {
    async list(signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/me/addresses',
        response: AddressListResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },

    async create(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/me/addresses',
        body,
        response: AddressSchema,
      });
      return data;
    },

    async update(address, body) {
      const { data } = await core.request({
        method: 'PATCH',
        path: `/v1/me/addresses/${address.id}`,
        body,
        response: AddressSchema,
        ifMatch: addressIfMatch(address),
      });
      return data;
    },

    async remove(address) {
      await core.request({
        method: 'DELETE',
        path: `/v1/me/addresses/${address.id}`,
        // `null` = `204 No Content`. Um schema aqui tentaria validar corpo vazio e falharia.
        response: null,
        ifMatch: addressIfMatch(address),
      });
    },
  };
}
